import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { SITE } from "@/lib/api";
import { NeighborhoodMarquee } from "@/components/Hero";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PRIMARY_NAV = [
  { to: "/lifestyles", label: "Lifestyles" },
  { to: "/home-value", label: "Home Value" },
  { to: "/coaching", label: "Coaching" },
  { to: "/escrow", label: "Escrow" },
  { to: "/resources", label: "Resources" },
];

const MORE_NAV = [
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/deal-room", label: "Deal Room" },
  { to: "/insider-resources", label: "Insider Resources" },
  { to: "/join-team", label: "Join the Team" },
  { to: "/community", label: "Events" },
  { to: "/client-library", label: "Client Library" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isMoreActive = MORE_NAV.some((n) => location.pathname.startsWith(n.to));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#F9F8F6]/85 border-b border-black/5" : "bg-transparent"
      }`}
    >
      {location.pathname === "/" && <NeighborhoodMarquee />}
      <div className="container-edge h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo-link" className="flex flex-col leading-none group">
          <span
            className="text-xl md:text-2xl tracking-wide flex items-center gap-2"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            <span>Champagne</span>
            <span className="text-sand text-lg" style={{ fontStyle: "italic" }}>&amp;</span>
            <span className="text-sand">Sunshine</span>
          </span>
          <span className="mt-1 text-[10px] tracking-[0.28em] uppercase text-soft">
            Celebrate every move · Los Angeles
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-6 2xl:gap-8">
          {PRIMARY_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `nav-link whitespace-nowrap ${isActive ? "active" : ""}`}
            >
              {n.label}
            </NavLink>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-more-trigger"
              className={`nav-link flex items-center gap-1 outline-none ${isMoreActive ? "active" : ""}`}
            >
              More <ChevronDown size={14} />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#F9F8F6] border border-[#E5E1DA] rounded-none min-w-[200px] shadow-lg"
              data-testid="nav-more-menu"
            >
              {MORE_NAV.map((n) => (
                <DropdownMenuItem key={n.to} asChild className="rounded-none focus:bg-[#F0EBE1]">
                  {n.external ? (
                    <a
                      href={n.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`nav-more-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-sm py-2.5 px-3 block w-full cursor-pointer"
                    >
                      {n.label}
                    </a>
                  ) : (
                    <Link
                      to={n.to}
                      data-testid={`nav-more-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-sm py-2.5 px-3 block w-full cursor-pointer"
                    >
                      {n.label}
                    </Link>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="hidden xl:flex items-center gap-4">
          <a
            href={SITE.teamPhoneHref}
            data-testid="nav-phone-link"
            className="hidden 2xl:flex text-sm items-center gap-2 text-soft hover:text-ocean transition-colors whitespace-nowrap"
          >
            <Phone size={14} /> {SITE.teamPhone}
          </a>
          <Link to="/contact" className="btn-primary whitespace-nowrap" data-testid="nav-contact-btn">
            Work With Us
          </Link>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="xl:hidden p-2"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-[#F9F8F6] border-t border-soft" data-testid="mobile-menu">
          <div className="container-edge py-6 flex flex-col gap-4">
            {[...PRIMARY_NAV, ...MORE_NAV].map((n) =>
              n.external ? (
                <a
                  key={n.to}
                  href={n.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-base text-ocean"
                >
                  {n.label}
                </a>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) => `text-base ${isActive ? "text-sand" : "text-ocean"}`}
                >
                  {n.label}
                </NavLink>
              )
            )}
            <Link to="/contact" className="btn-primary mt-2 self-start" data-testid="mobile-nav-contact-btn">
              Work With Us
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
